import { useEffect, useRef } from "react";

export type IrisMode = "idle" | "listening" | "thinking" | "speaking";

const BLADES = 8;
const CAPTIONS: Record<IrisMode, string> = {
  idle: "",
  listening: "Listening",
  thinking: "Thinking",
  speaking: "Speaking",
};

/**
 * Caprica's presence: a camera iris in gold light. It breathes at rest, opens to the maker's
 * voice, turns its blades while it thinks and pulses with each word it speaks.
 * `level` is read every frame (0..1) so voice and speech drive it without re-rendering.
 */
export function CapricaIris({
  mode,
  level,
  size = 96,
}: {
  mode: IrisMode;
  level?: { current: number };
  size?: number;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const shell = useRef<HTMLDivElement>(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;

  useEffect(() => {
    const element = canvas.current;
    const context = element?.getContext("2d");
    if (!element || !context) return;
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    element.width = size * dpr;
    element.height = size * dpr;
    let open = 0.34;
    let openVelocity = 0;
    let glow = 0.4;
    let spin = 0;
    let spinVelocity = 0.05;
    // The voice as a smooth envelope: it opens quickly on a syllable and settles gently.
    let envelope = 0;
    // How much Iris is thinking right now, faded in and out rather than switched.
    let thought = 0;
    let last = performance.now();
    let frame = 0;

    const draw = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const t = now / 1000;
      const current = modeRef.current;
      const voice = Math.max(0, Math.min(1, level?.current ?? 0));
      envelope += (voice - envelope) * Math.min(1, dt * (voice > envelope ? 14 : 3.8));
      thought += ((current === "thinking" ? 1 : 0) - thought) * Math.min(1, dt * 3.5);
      let targetOpen = 0.34 + 0.035 * Math.sin(t * 1.3);
      let targetGlow = 0.38 + 0.06 * Math.sin(t * 1.3);
      let spinSpeed = 0.05;
      if (current === "listening") {
        targetOpen = 0.4 + envelope * 0.5;
        targetGlow = 0.5 + envelope * 0.5;
        spinSpeed = 0.12;
      } else if (current === "thinking") {
        // A slow, deep breath while she focuses: calm, clearly alive.
        targetOpen = 0.29 + 0.06 * Math.sin(t * 2.1);
        targetGlow = 0.5 + 0.2 * (0.5 + 0.5 * Math.sin(t * 2.1));
        spinSpeed = 0.45;
      } else if (current === "speaking") {
        targetOpen = 0.42 + envelope * 0.46;
        targetGlow = 0.6 + envelope * 0.4;
        spinSpeed = 0.16;
      }
      if (still) {
        targetOpen = current === "idle" ? 0.34 : 0.55;
        targetGlow = current === "idle" ? 0.4 : 0.75;
        spinSpeed = 0;
      }
      // A gently damped spring: the blades glide to where they are going, never snap.
      const stiffness = 70;
      const damping = 2 * Math.sqrt(stiffness) * 0.9;
      openVelocity += (stiffness * (targetOpen - open) - damping * openVelocity) * dt;
      open = Math.max(0.16, Math.min(0.95, open + openVelocity * dt));
      glow += (targetGlow - glow) * Math.min(1, dt * 5);
      spinVelocity += (spinSpeed - spinVelocity) * Math.min(1, dt * 2.2);
      spin += spinVelocity * dt;
      shell.current?.style.setProperty("--iris-glow", glow.toFixed(3));

      const w = element.width;
      const c = w / 2;
      const radius = c - 2 * dpr;
      const hole = radius * open;
      const turn = spin + (1 - open) * 0.95;
      context.clearRect(0, 0, w, w);
      context.save();
      context.beginPath();
      context.arc(c, c, radius, 0, Math.PI * 2);
      context.clip();

      // The light behind the lens.
      const light = context.createRadialGradient(c, c, 0, c, c, radius);
      light.addColorStop(0, `rgba(255, 246, 228, ${0.55 + glow * 0.45})`);
      light.addColorStop(0.35, `rgba(228, 194, 142, ${0.35 + glow * 0.5})`);
      light.addColorStop(1, "rgba(120, 86, 40, 0.2)");
      context.fillStyle = light;
      context.fillRect(0, 0, w, w);

      // The blades: everything outside the octagonal opening.
      const points: [number, number][] = [];
      for (let i = 0; i < BLADES; i++) {
        const angle = turn + (i / BLADES) * Math.PI * 2;
        points.push([c + Math.cos(angle) * hole, c + Math.sin(angle) * hole]);
      }
      context.beginPath();
      context.arc(c, c, radius + 1, 0, Math.PI * 2);
      context.moveTo(points[0]![0], points[0]![1]);
      for (const [x, y] of points.slice(1)) context.lineTo(x, y);
      context.closePath();
      const metal =
        typeof context.createConicGradient === "function"
          ? context.createConicGradient(turn, c, c)
          : (context as CanvasRenderingContext2D).createLinearGradient(0, 0, w, w);
      for (let i = 0; i <= BLADES; i++) {
        metal.addColorStop(i / BLADES, "#1d1812");
        if (i < BLADES) metal.addColorStop((i + 0.5) / BLADES, "#0e0c0a");
      }
      context.fillStyle = metal;
      context.fill("evenodd");

      // Blade edges run from each corner of the opening out to the rim.
      context.strokeStyle = `rgba(228, 194, 142, ${0.22 + glow * 0.3})`;
      context.lineWidth = dpr;
      for (let i = 0; i < BLADES; i++) {
        const [x0, y0] = points[i]!;
        const [x1, y1] = points[(i + 1) % BLADES]!;
        const dx = x1 - x0;
        const dy = y1 - y0;
        const length = Math.hypot(dx, dy) || 1;
        const ux = dx / length;
        const uy = dy / length;
        const px = x0 - c;
        const py = y0 - c;
        const b = px * ux + py * uy;
        const reach = -b + Math.sqrt(Math.max(0, b * b - (px * px + py * py - radius * radius)));
        context.beginPath();
        context.moveTo(x0, y0);
        context.lineTo(x0 + ux * reach, y0 + uy * reach);
        context.stroke();
      }
      context.restore();

      // The barrel ring.
      context.beginPath();
      context.arc(c, c, radius - dpr * 0.5, 0, Math.PI * 2);
      context.strokeStyle = `rgba(201, 163, 107, ${0.45 + glow * 0.4})`;
      context.lineWidth = 1.5 * dpr;
      context.stroke();

      // While she thinks, a soft light travels around the barrel like a lens finding focus:
      // unmistakably still working, never a spinner.
      if (thought > 0.02) {
        const head = t * 2.6;
        const tail = 1.35;
        const steps = 28;
        const ring = radius - 1.2 * dpr;
        context.lineCap = "round";
        for (let i = 0; i < steps; i++) {
          const fade = (1 - i / steps) ** 1.8;
          const from = head - (i + 1) * (tail / steps);
          const to = head - i * (tail / steps);
          context.beginPath();
          context.arc(c, c, ring, from, to);
          context.strokeStyle = `rgba(255, 236, 200, ${(thought * fade * 0.95).toFixed(3)})`;
          context.lineWidth = (1.2 + 2.2 * fade) * dpr;
          context.stroke();
        }
        const hx = c + Math.cos(head) * ring;
        const hy = c + Math.sin(head) * ring;
        const spark = context.createRadialGradient(hx, hy, 0, hx, hy, 5 * dpr);
        spark.addColorStop(0, `rgba(255, 248, 232, ${thought.toFixed(3)})`);
        spark.addColorStop(1, "rgba(255, 236, 200, 0)");
        context.fillStyle = spark;
        context.beginPath();
        context.arc(hx, hy, 5 * dpr, 0, Math.PI * 2);
        context.fill();
      }

      if (!document.hidden) frame = requestAnimationFrame(draw);
    };
    const resume = () => {
      if (!document.hidden) {
        cancelAnimationFrame(frame);
        last = performance.now();
        frame = requestAnimationFrame(draw);
      }
    };
    frame = requestAnimationFrame(draw);
    document.addEventListener("visibilitychange", resume);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [size, level]);

  return (
    <div ref={shell} className="luna-iris" data-mode={mode} style={{ width: size, height: size }}>
      <canvas ref={canvas} style={{ width: size, height: size }} aria-hidden="true" />
      <span className="luna-iris-caption" aria-live="polite">
        {CAPTIONS[mode]}
      </span>
    </div>
  );
}
